import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	faCircleCheck,
	faCircleExclamation,
	faCirclePause,
	faCirclePlay,
	faSpinner,
	IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

import { CampaignDisplayStatus } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

const PILL_BASE =
	"inline-flex items-center justify-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold min-w-[96px]";

/** Static pill colors per non-toggleable status. */
const STATIC_PILL: Record<
	CampaignDisplayStatus,
	{ classes: string; icon: IconDefinition; iconClass?: string }
> = {
	[CampaignDisplayStatus.INVALID]: {
		classes: "bg-status-error-light text-status-error border-status-error",
		icon: faCircleExclamation,
	},
	[CampaignDisplayStatus.VALID]: {
		classes: "bg-blue-action-200 text-blue-action border-blue-action",
		icon: faCircleCheck,
	},
	// READY is rendered as a clickable "Activate" pill (see TOGGLE_CONFIG); this is the fallback
	// styling for the rare render without an onToggle handler.
	[CampaignDisplayStatus.READY]: {
		classes: "bg-blue-action-200 text-blue-action border-blue-action",
		icon: faCircleCheck,
	},
	[CampaignDisplayStatus.ENDED]: {
		classes: "bg-gray-100 text-gray-500 border-gray-300",
		icon: faCircleCheck,
	},
	[CampaignDisplayStatus.ACTIVE]: {
		classes: "bg-status-success-light text-status-success border-status-success",
		icon: faCirclePlay,
	},
	[CampaignDisplayStatus.PAUSED]: {
		classes: "bg-status-warning-light text-status-warning border-status-warning",
		icon: faCirclePause,
	},
	// Transient activation state: a spinning pill that isn't clickable until it resolves to ACTIVE.
	[CampaignDisplayStatus.LOADING]: {
		classes: "bg-blue-action-200 text-blue-action border-blue-action",
		icon: faSpinner,
		iconClass: "animate-spin",
	},
};

/**
 * Clickable run-status pills: each shows its current label at rest and the action it performs on
 * hover. READY (configured + leads, never activated) and PAUSED both activate → RUNNING; ACTIVE
 * pauses. Non-toggleable statuses fall through to STATIC_PILL.
 */
const TOGGLE_CONFIG: Partial<
	Record<
		CampaignDisplayStatus,
		{ classes: string; restIcon: IconDefinition; actionIcon: IconDefinition; action: string }
	>
> = {
	[CampaignDisplayStatus.ACTIVE]: {
		classes:
			"bg-status-success-light text-status-success border-status-success hover:bg-status-warning-light hover:text-status-warning hover:border-status-warning",
		restIcon: faCirclePlay,
		actionIcon: faCirclePause,
		action: "Pause",
	},
	[CampaignDisplayStatus.PAUSED]: {
		classes:
			"bg-status-warning-light text-status-warning border-status-warning hover:bg-status-success-light hover:text-status-success hover:border-status-success",
		restIcon: faCirclePause,
		actionIcon: faCirclePlay,
		action: "Resume",
	},
	[CampaignDisplayStatus.READY]: {
		classes:
			"bg-blue-action-200 text-blue-action border-blue-action hover:bg-status-success-light hover:text-status-success hover:border-status-success",
		restIcon: faCircleCheck,
		actionIcon: faCirclePlay,
		action: "Activate",
	},
};

interface CampaignStatusPillProps {
	status: CampaignDisplayStatus;
	disabled?: boolean;
	onToggle?: () => void;
	/**
	 * Why this campaign cannot run (PREC-3952). Shown in place of the generic status tooltip, so
	 * INVALID stops being a dead end: the badge that blocks the user also tells them what to fix.
	 */
	reasons?: string[];
}

/**
 * Campaign status as a colored pill (mockup parity). ACTIVE/PAUSED/READY are clickable and swap to
 * their action ("Pause"/"Resume"/"Activate") on hover (the run-status toggle); the other states
 * show an explanatory tooltip.
 */
export const CampaignStatusPill: FunctionComponent<CampaignStatusPillProps> = ({
	status,
	disabled = false,
	onToggle,
	reasons,
}) => {
	const { t } = useTranslation();
	const label = t(`campaign.builder.status.labels.${status}`);

	const toggle = TOGGLE_CONFIG[status];

	if (toggle && onToggle) {
		return (
			<button
				className={classNames(
					PILL_BASE,
					"group transition-colors",
					toggle.classes,
					disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
				)}
				disabled={disabled}
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onToggle();
				}}
			>
				<span className="inline-flex items-center gap-1.5 group-hover:hidden">
					<Icon className="w-3.5 h-3.5" icon={toggle.restIcon} />
					{label}
				</span>
				<span className="hidden items-center gap-1.5 group-hover:inline-flex">
					<Icon className="w-3.5 h-3.5" icon={toggle.actionIcon} />
					{toggle.action}
				</span>
			</button>
		);
	}

	const config = STATIC_PILL[status];

	const tooltip = reasons?.length ? (
		<ul className="list-disc space-y-0.5 pl-4 text-left">
			{reasons.map((reason) => (
				<li key={reason}>{reason}</li>
			))}
		</ul>
	) : (
		t(`campaign.builder.status.tooltips.${status}`)
	);

	return (
		<Tooltip content={tooltip} direction="bottom" variant="dark">
			<div className={classNames(PILL_BASE, "cursor-default", config.classes)} tabIndex={0}>
				<Icon className={classNames("w-3.5 h-3.5", config.iconClass)} icon={config.icon} />
				{label}
			</div>
		</Tooltip>
	);
};
